import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft, Sparkles, RotateCcw, Home, ArrowRight,
  Clock, CheckCircle, XCircle, BookOpen, Target, Zap,
} from "lucide-react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import axios from "axios";
import API_BASE_URL from "../Config/config";

// ── score helpers ─────────────────────────────────────────────────────────────
const getScoreColor  = (p) => p >= 90 ? '#10B981' : p >= 80 ? '#8B5CF6' : p >= 60 ? '#F59E0B' : '#EF4444';
const getScoreLabel  = (p) => p >= 90 ? 'Outstanding' : p >= 80 ? 'Excellent' : p >= 60 ? 'Good' : 'Needs Work';
const getPerfMessage = (p) => {
  if (p >= 95) return { title: 'Outstanding!',    sub: "You're demonstrating mastery of this topic." };
  if (p >= 80) return { title: 'Excellent work.', sub: "You're ready for the next level." };
  if (p >= 60) return { title: 'Good effort.',    sub: 'Review a few concepts before progressing.' };
  return         { title: "Don't worry.",         sub: "Practice again and you'll improve quickly." };
};

// ── ScoreCircle ───────────────────────────────────────────────────────────────
const ScoreCircle = ({ percentage }) => {
  const r    = 60;
  const circ = 2 * Math.PI * r;
  const col  = getScoreColor(percentage);
  const lbl  = getScoreLabel(percentage);
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-40">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
          <circle cx="80" cy="80" r={r} fill="none" stroke="#F3F4F6" strokeWidth="12" />
          <motion.circle
            cx="80" cy="80" r={r}
            fill="none" stroke={col} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={circ}
            initial={{ strokeDashoffset: circ }}
            animate={{ strokeDashoffset: circ * (1 - percentage / 100) }}
            transition={{ duration: 1.4, ease: 'easeOut', delay: 0.6 }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <motion.span
            className="text-3xl font-black leading-none"
            style={{ color: col }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.4, type: 'spring' }}
          >
            {percentage.toFixed(0)}%
          </motion.span>
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mt-1">{lbl}</span>
        </div>
      </div>
    </div>
  );
};

// ── StatCard ──────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, iconBg, iconColor, delay }) => (
  <motion.div
    className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col items-center gap-2 cursor-default"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    whileHover={{ y: -3, boxShadow: '0 8px 24px rgba(109,40,217,0.10)' }}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
      <span className={iconColor}>{icon}</span>
    </div>
    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide text-center leading-tight">{label}</span>
    <span className="text-2xl font-black text-gray-900">{value}</span>
  </motion.div>
);

// ── AnimatedStar ──────────────────────────────────────────────────────────────
const AnimatedStar = ({ filled, delay }) => (
  <motion.span
    className={`text-4xl select-none ${filled ? 'text-amber-400' : 'text-gray-200'}`}
    initial={{ opacity: 0, scale: 0.3, rotate: -30 }}
    animate={{ opacity: 1, scale: 1, rotate: 0 }}
    transition={{ delay, type: 'spring', stiffness: 220, damping: 12 }}
    style={{ filter: filled ? 'drop-shadow(0 2px 10px rgba(251,191,36,0.6))' : 'none' }}
  >
    ★
  </motion.span>
);

// ── LevelClearance ────────────────────────────────────────────────────────────
const LevelClearance = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    answers       = {},
    technology    = "HTML",
    level         = "1",
    user_id       = "Guest",
    time          = "00:00",
    time_taken    = "60",
    totalQuestions = 0,
  } = location.state || {};

  const nextLevel = parseInt(level) + 1;

  const [explanation, setExplanation]           = useState("");
  const [showExplanation, setShowExplanation]   = useState(false);
  const [loading, setLoading]                   = useState(false);
  const [result, setResult]                     = useState(null);
  const [initialLoading, setInitialLoading]     = useState(true);
  const [incorrectQuestionIds, setIncorrectQuestionIds] = useState([]);

  // ── evaluate via API (unchanged) ─────────────────────────────────────────
  useEffect(() => {
    const evaluateAutomatically = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/evaluate`, {
          answers, technology, level, user_id, time_taken, totalQuestions,
        });
        setResult({ correct: res.data.correct, wrong: res.data.wrong, score: res.data.score });
        if (res.data.feedback) setExplanation(res.data.feedback);
        if (res.data.incorrect_questions) setIncorrectQuestionIds(res.data.incorrect_questions);
      } catch (err) {
        console.error("❌ Evaluation failed", err);
      } finally {
        setInitialLoading(false);
      }
    };
    evaluateAutomatically();
  }, [answers, technology, level, user_id, time_taken, totalQuestions]);

  // ── derived values ────────────────────────────────────────────────────────
  const totalForCalc = totalQuestions > 0 ? totalQuestions : (result ? result.correct + result.wrong : 1);
  const percentage   = result ? (result.correct / totalForCalc) * 100 : 0;
  const passed       = percentage >= 80;
  const stars        = [percentage >= 30, percentage >= 60, percentage >= 80];
  const perfMsg      = getPerfMessage(percentage);
  const scoreCol     = getScoreColor(percentage);

  // ── confetti on pass ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialLoading && passed) {
      const fire = (o) => confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: ['#8B5CF6','#6D28D9','#10B981','#F59E0B'], ...o });
      setTimeout(() => fire({}), 600);
      setTimeout(() => fire({ angle: 60,  spread: 55, origin: { x: 0 } }), 1000);
      setTimeout(() => fire({ angle: 120, spread: 55, origin: { x: 1 } }), 1300);
    }
  }, [initialLoading, passed]);

  // ── toggle inline feedback panel ─────────────────────────────────────────
  const fetchExplanation = () => setShowExplanation((prev) => !prev);
  const goToNextLevel    = () => navigate(`/questions/${technology}/${nextLevel}`, { state: { mode: location.state?.mode || 'practice' } });
  const retryLevel       = () => navigate(`/questions/${technology}/${level}`,     { state: { mode: location.state?.mode || 'practice' } });
  const goToSkillTree    = () => navigate('/techgrid');
  const goToPracticeHome = () => navigate('/techgrid');

  // ── loading state ─────────────────────────────────────────────────────────
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50/30 to-indigo-50 flex flex-col items-center justify-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-200">
          <Sparkles className="text-white" size={28} />
        </div>
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2.5 h-2.5 rounded-full bg-violet-500"
              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
              transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.2 }}
            />
          ))}
        </div>
        <p className="text-gray-500 font-semibold text-sm">Evaluating your results…</p>
      </div>
    );
  }

  // ── main render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50/20 to-indigo-50 flex flex-col">

      {/* Header */}
      <header className="h-16 flex items-center justify-between px-6 sticky top-0 z-50 border-b backdrop-blur-md bg-white/80 border-gray-100/80">
        <div className="flex items-center gap-3">
          <button
            onClick={goToPracticeHome}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shadow-md shadow-violet-200">
            <SparklesIcon className="text-white h-4 w-4" />
          </div>
          <span className="text-base font-bold text-gray-900 tracking-tight">TheSkillGenie</span>
        </div>
        <span className="text-sm text-gray-400 font-medium hidden sm:block">
          {technology} &middot; Level {level}
        </span>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-start justify-center p-4 sm:p-8">
        <motion.article
          className="w-full max-w-[900px] bg-white/95 backdrop-blur-sm rounded-3xl shadow-[0_20px_60px_rgba(109,40,217,0.10)] border border-violet-100/60 overflow-hidden"
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {/* Accent bar */}
          <div className={`h-1.5 w-full ${passed ? 'bg-gradient-to-r from-violet-500 via-purple-500 to-emerald-400' : 'bg-gradient-to-r from-red-400 via-rose-400 to-orange-400'}`} />

          <div className="p-6 sm:p-10">

            {/* ── STATUS BADGE ── */}
            <motion.div
              className="flex justify-center mb-6"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <span className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider border ${
                passed
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-red-50   text-red-600   border-red-200'
              }`}>
                <span className={`w-2 h-2 rounded-full ${passed ? 'bg-emerald-500' : 'bg-red-500'} animate-pulse`} />
                {passed ? 'Level Completed' : 'Level Incomplete'}
              </span>
            </motion.div>

            {/* ── STARS ── */}
            <div className="flex justify-center gap-3 mb-5">
              {stars.map((filled, i) => <AnimatedStar key={i} filled={filled} delay={0.3 + i * 0.15} />)}
            </div>

            {/* ── TITLE ── */}
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              <h1 className="text-[38px] sm:text-[42px] font-black text-gray-900 leading-tight tracking-tight">
                {passed ? '🎉 Congratulations!' : 'Almost There!'}
              </h1>
              <p className="text-gray-500 mt-2 text-base max-w-md mx-auto">
                {passed
                  ? "You've unlocked the next level."
                  : "You're only a few correct answers away from unlocking the next level."}
              </p>
            </motion.div>

            {/* ── SCORE CIRCLE ── */}
            <div className="flex justify-center mb-8">
              <ScoreCircle percentage={percentage} />
            </div>

            {/* ── STATS GRID 2×3 ── */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
              <StatCard icon={<BookOpen size={18} />}    label="Total Questions"   value={totalForCalc}              iconBg="bg-violet-50"  iconColor="text-violet-600"  delay={0.65} />
              <StatCard icon={<Target size={18} />}      label="Attempted"         value={result ? result.correct + result.wrong : 0} iconBg="bg-indigo-50"  iconColor="text-indigo-600"  delay={0.70} />
              <StatCard icon={<CheckCircle size={18} />} label="Correct Answers"   value={result?.correct ?? 0}      iconBg="bg-emerald-50" iconColor="text-emerald-600" delay={0.75} />
              <StatCard icon={<XCircle size={18} />}     label="Incorrect Answers" value={result?.wrong ?? 0}        iconBg="bg-red-50"     iconColor="text-red-500"     delay={0.80} />
              <StatCard icon={<Zap size={18} />}         label="Score"             value={`${percentage.toFixed(1)}%`} iconBg="bg-amber-50" iconColor="text-amber-500"   delay={0.85} />
              <StatCard icon={<Clock size={18} />}       label="Time Taken"        value={time}                      iconBg="bg-blue-50"    iconColor="text-blue-500"    delay={0.90} />
            </div>

            {/* ── PERFORMANCE MESSAGE ── */}
            <motion.div
              className={`rounded-2xl px-5 py-4 mb-6 border ${
                percentage >= 80
                  ? 'bg-emerald-50 border-emerald-100'
                  : percentage >= 60
                  ? 'bg-amber-50 border-amber-100'
                  : 'bg-red-50 border-red-100'
              }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.95 }}
            >
              <p className={`font-bold text-sm ${percentage >= 80 ? 'text-emerald-800' : percentage >= 60 ? 'text-amber-800' : 'text-red-800'}`}>
                {perfMsg.title}
              </p>
              <p className={`text-sm mt-0.5 ${percentage >= 80 ? 'text-emerald-700' : percentage >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                {perfMsg.sub}
              </p>
            </motion.div>

            {/* ── PROGRESS BAR ── */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Progress to Unlock</span>
                <span className="text-xs font-bold text-gray-600">
                  {passed ? '100%' : `${percentage.toFixed(0)}% / 80%`}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${passed ? 'bg-gradient-to-r from-violet-500 to-emerald-400' : 'bg-gradient-to-r from-violet-400 to-indigo-400'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(percentage, 100)}%` }}
                  transition={{ delay: 1.1, duration: 1.1, ease: 'easeOut' }}
                />
              </div>
            </motion.div>

            {/* ── ANSWER SUMMARY ── */}
            <motion.section
              className="bg-gray-50 rounded-2xl p-5 mb-8 border border-gray-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05 }}
              aria-label="Question summary"
            >
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Question Summary</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Answered Correct',   value: result?.correct ?? 0,         color: 'text-emerald-600' },
                  { label: 'Answered Incorrect',  value: result?.wrong ?? 0,           color: 'text-red-500' },
                  { label: 'Accuracy',            value: `${percentage.toFixed(0)}%`,  color: 'text-violet-600' },
                  { label: 'Completion',          value: '100%',                       color: 'text-blue-500' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="bg-white rounded-xl p-3 text-center border border-gray-100 shadow-sm">
                    <p className={`text-xl font-black ${color}`}>{value}</p>
                    <p className="text-xs text-gray-400 font-medium mt-0.5">{label}</p>
                  </div>
                ))}
              </div>
            </motion.section>

            {/* ── ACTION BUTTONS ── */}
            <motion.div
              className="flex flex-col sm:flex-row gap-3 flex-wrap"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15 }}
            >
              {passed ? (
                <>
                  <button
                    onClick={goToNextLevel}
                    className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold rounded-2xl shadow-lg shadow-violet-200 hover:brightness-110 transition-all text-sm"
                  >
                    <ArrowRight size={18} /> Continue to Level {nextLevel}
                  </button>
                  <button
                    onClick={fetchExplanation}
                    className="flex-1 min-w-[160px] flex items-center justify-center gap-2 px-6 py-4 bg-violet-50 text-violet-700 font-bold rounded-2xl hover:bg-violet-100 transition-all border border-violet-200 text-sm"
                  >
                    <Sparkles size={18} /> Evaluate &amp; Explain
                  </button>
                  <button
                    onClick={retryLevel}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 text-gray-700 font-bold rounded-2xl hover:bg-gray-100 transition-all border border-gray-200 text-sm"
                  >
                    <RotateCcw size={18} /> Practice Again
                  </button>
                  <button
                    onClick={goToPracticeHome}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 text-gray-500 font-bold rounded-2xl hover:bg-gray-100 transition-all border border-gray-200 text-sm"
                  >
                    <Home size={18} /> Back to Skill Tree
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={fetchExplanation}
                    className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold rounded-2xl shadow-lg shadow-violet-200 hover:brightness-110 transition-all text-sm"
                  >
                    <Sparkles size={18} /> {loading ? 'Analyzing…' : 'Evaluate & Explain'}
                  </button>
                  <button
                    onClick={retryLevel}
                    className="flex-1 min-w-[160px] flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 text-gray-700 font-bold rounded-2xl hover:bg-gray-100 transition-all border border-gray-200 text-sm"
                  >
                    <RotateCcw size={18} /> Retry Level
                  </button>
                  <button
                    onClick={goToPracticeHome}
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gray-50 text-gray-500 font-bold rounded-2xl hover:bg-gray-100 transition-all border border-gray-200 text-sm"
                  >
                    <Home size={18} /> Back to Skill Tree
                  </button>
                </>
              )}
            </motion.div>

            {/* AI Feedback Panel */}
            {showExplanation && (
              <motion.div
                className="mt-8 text-left bg-violet-50 rounded-2xl p-6 border border-violet-100"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 bg-violet-600 rounded-xl flex items-center justify-center text-white">
                    <Sparkles size={18} />
                  </div>
                  <h3 className="font-bold text-violet-900 text-sm uppercase tracking-wider">AI Expert Analysis</h3>
                </div>
                {explanation ? (
                  <p className="text-violet-800 font-medium leading-relaxed whitespace-pre-line text-sm pl-4 border-l-4 border-violet-300 mb-4">
                    {explanation}
                  </p>
                ) : (
                  <p className="text-violet-500 text-sm italic pl-4 border-l-4 border-violet-200">
                    Feedback is being generated…
                  </p>
                )}
                {incorrectQuestionIds.length > 0 && (
                  <div className="pt-4 border-t border-violet-200">
                    <p className="text-violet-700 text-xs font-bold uppercase tracking-widest mb-3">Focus Areas</p>
                    <div className="flex flex-wrap gap-2">
                      {incorrectQuestionIds.map((qId) => (
                        <span key={qId} className="bg-white text-violet-600 border border-violet-200 px-4 py-1.5 rounded-xl font-bold text-xs shadow-sm">
                          Q{qId}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

          </div>
        </motion.article>
      </main>
    </div>
  );
};

export default LevelClearance;
